import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, UserCircle2 } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";
import { useAuth } from "@/context/AuthContext";
import AuthModal from "@/components/AuthModal";

const links = [
  { label: "The Rules", id: "rules" },
  { label: "How It Works", id: "how-it-works" },
  { label: "Shift Board", id: "shifts" },
  { label: "Trust", id: "trust" },
  { label: "FAQ", id: "faq" },
];

export default function Navbar() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [authOpen, setAuthOpen] = useState(false);

  return (
    <header data-testid="navbar" className="fixed top-0 left-0 right-0 z-50 border-b border-white/5 bg-[#07090e]/80 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-12">
        <button
          data-testid="navbar-logo"
          onClick={() => window.__lenis?.scrollTo(0)}
          className="flex items-center gap-2.5"
        >
          <span className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-md border border-amber-500/30">
            <img src="/icons/icon-192.png" alt="GuardGig28 logo" className="h-full w-full object-cover" />
          </span>
          <span className="font-display text-xl font-extrabold uppercase tracking-tight text-slate-50">
            Guard<span className="text-amber-500">Gig</span>28
          </span>
        </button>

        <nav className="hidden items-center gap-8 md:flex">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => scrollToSection(l.id)}
              className="font-mono-tag text-[11px] text-slate-400 transition-colors duration-200 hover:text-amber-500"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <div data-testid="live-status" className="hidden items-center gap-2 lg:flex">
            <span className="animate-pulse-dot h-2 w-2 rounded-full bg-emerald-500" />
            <span className="font-mono-tag text-[10px] text-emerald-400">Live shifts active</span>
          </div>
          {user ? (
            <button
              data-testid="navbar-account-btn"
              onClick={() => navigate("/account")}
              className="flex items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-2.5 font-display text-sm font-bold uppercase tracking-wide text-amber-500 transition-all hover:bg-amber-500 hover:text-[#07090e]"
            >
              <UserCircle2 className="h-4 w-4" />
              {user.name?.split(" ")[0] || "Account"}
            </button>
          ) : (
            <button
              data-testid="navbar-signin-btn"
              onClick={() => setAuthOpen(true)}
              className="font-mono-tag hidden text-[11px] text-slate-400 transition-colors hover:text-amber-500 sm:block"
            >
              Sign in
            </button>
          )}
          <button
            data-testid="navbar-download-btn"
            onClick={() => scrollToSection("download")}
            className="flex items-center gap-2 rounded-md bg-amber-500 px-4 py-2.5 font-display text-sm font-bold uppercase tracking-wide text-[#07090e] transition-all duration-200 hover:bg-amber-400 hover:shadow-[0_0_24px_rgba(245,158,11,0.4)]"
          >
            <Download className="h-4 w-4" strokeWidth={2.5} />
            Get the App
          </button>
        </div>
      </div>
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
    </header>
  );
}
